'use client';

import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef } from 'react';

import type { HTMLTagProps } from '../../../types';

const chipVariants = cva(
  'box-border flex cursor-pointer items-center rounded-pill border-1 px-12 py-6 font-500 transition-colors typo-body4',
  {
    variants: {
      theme: {
        primary: 'border-Gray-300 bg-Gray-white text-Gray-700',
        'primary-line': 'border-Gray-300 bg-Gray-white text-Gray-700',
        sub: 'border-Gray-300 bg-Gray-300 text-Gray-700',
      },
      checked: {
        // compoundVariants에서 처리하므로 빈문자열 클래스 할당
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        theme: 'primary',
        checked: true,
        className: 'border-Gray-900 bg-Gray-900 text-Gray-white',
      },
      {
        theme: 'primary-line',
        checked: true,
        className: 'border-Gray-900 text-Gray-900',
      },
      {
        theme: 'sub',
        checked: true,
        className: 'border-Gray-900 bg-Gray-900 text-Gray-white',
      },
    ],
    defaultVariants: {
      theme: 'primary',
      checked: false,
    },
  },
);

export interface ChipProps
  extends
    Omit<HTMLTagProps<'input'>, 'type' | 'checked'>,
    Omit<VariantProps<typeof chipVariants>, 'checked'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const Chip = forwardRef<HTMLInputElement, ChipProps>(
  (
    {
      theme,
      children,
      onChange,
      onCheckedChange,
      className,
      checked,
      ...restProps
    }: ChipProps,
    ref,
  ) => {
    return (
      <label className={cn(chipVariants({ theme, checked, className }))}>
        <input
          {...restProps}
          ref={ref}
          className="hidden"
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            onChange?.(e);
            onCheckedChange?.(e.target.checked);
          }}
        />
        {children}
      </label>
    );
  },
);

Chip.displayName = 'Chip';
